import type { SessionState } from '@avrahami/ui';
import type { QueryClient } from '@tanstack/react-query';
import { storeSessionHeader, tabStore } from './store-path';

/**
 * Every store has a session cookie of its own, shared by that store's tabs (adr/0033). When one
 * tab signs in, out, or enters a store, the other tabs of the stores it touched must not keep
 * showing (or acting on) what was there: the change is announced on a BroadcastChannel with the
 * stores it touched, and every tab of those stores drops its cache and reads the session again.
 * Tabs of other stores are left alone.
 */
const CHANNEL = 'avrahami-tenant-admin-session';
export const SESSION_KEY = ['session'] as const;

interface Announcement {
  kind: 'session-changed';
  /** The sessions touched: store ids, and `none` for the tabs without a store. */
  stores: string[];
}

const ownSlot = storeSessionHeader(tabStore);

let channel: BroadcastChannel | null = null;

function openChannel(): BroadcastChannel | null {
  if (channel === null && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL);
  }
  return channel;
}

/** Drops every cached answer except the session itself (whose observers stay attached). */
function dropStoreData(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_KEY[0] });
}

/**
 * This tab changed a session: start fresh here with the new state, and tell the tabs of the
 * stores touched (this tab's, and the store the new state is in, unless `stores` says which).
 */
export function sessionChanged(
  queryClient: QueryClient,
  state: SessionState,
  stores?: readonly string[],
): void {
  dropStoreData(queryClient);
  queryClient.setQueryData(SESSION_KEY, state);
  const reached = state.kind === 'signed-in' ? state.me.activeTenant?.tenantId : undefined;
  announceTo(stores ?? [ownSlot, ...(reached === undefined ? [] : [reached])]);
}

/** Tells the tabs of these stores that their session changed; this tab's state stays. */
export function announceTo(stores: readonly string[]): void {
  const announcement: Announcement = { kind: 'session-changed', stores: [...new Set(stores)] };
  openChannel()?.postMessage(announcement);
}

/** Another tab changed the session (or the server says so): drop everything and re-read it. */
export function reloadSession(queryClient: QueryClient): void {
  dropStoreData(queryClient);
  void queryClient.invalidateQueries({ queryKey: SESSION_KEY });
}

/** Listens for other tabs' announcements; returns the unsubscribe function. */
export function listenToOtherTabs(queryClient: QueryClient): () => void {
  const open = openChannel();
  if (open === null) return () => undefined;
  const onMessage = (event: MessageEvent<unknown>) => {
    const data = event.data;
    if (typeof data !== 'object' || data === null || !('stores' in data)) return;
    const { stores } = data as Partial<Announcement>;
    if (Array.isArray(stores) && stores.includes(ownSlot)) reloadSession(queryClient);
  };
  open.addEventListener('message', onMessage);
  return () => {
    open.removeEventListener('message', onMessage);
  };
}
